import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, Timer, Users, ShieldCheck, Weight, Ruler, Check, Truck, Puzzle, ChevronLeft, ChevronRight } from "lucide-react";
import { SizeDrawing } from "../components/SizeDrawing";
import { useProduct, inr, pctOff, buildLabel, CATEGORY_NAMES } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Stars } from "../components/Stars";
import { QtyStepper } from "../components/CartDrawer";
import { PincodeChecker } from "../components/PincodeChecker";
import { Reviews } from "../components/Reviews";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Motion";
import { Skeleton } from "../components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Checkbox } from "../components/ui/checkbox";
import { Seo, breadcrumbLd, productLd, SITE_URL } from "../components/Seo";
import { ShareBar } from "../components/ShareBar";

const Gallery = ({ images, name }) => {
  const [i, setI] = useState(0);
  useEffect(() => setI(0), [images]);
  const step = (d) => setI((x) => (x + d + images.length) % images.length);
  return (
    <div className="flex flex-col gap-3 lg:sticky lg:top-28">
      <div className="render aspect-square rounded-[1.5rem] sm:rounded-[2rem]">
        <AnimatePresence mode="sync" initial={false}>
          <motion.img key={images[i]} data-testid="product-main-image" src={images[i]} alt={name} initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="absolute inset-0 h-full w-full object-cover" />
        </AnimatePresence>
        {images.length > 1 && (
          <>
            <button data-testid="gallery-prev" onClick={() => step(-1)} aria-label="Previous image" className="absolute left-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-linen/95 shadow-sm backdrop-blur transition-transform hover:scale-105"><ChevronLeft className="h-5 w-5" /></button>
            <button data-testid="gallery-next" onClick={() => step(1)} aria-label="Next image" className="absolute right-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-linen/95 shadow-sm backdrop-blur transition-transform hover:scale-105"><ChevronRight className="h-5 w-5" /></button>
          </>
        )}
        <span className="absolute bottom-4 left-4 rounded-full bg-linen/95 px-3 py-1 text-xs font-semibold backdrop-blur">{i === 0 ? "Studio view" : "In a Hyderabad home"}</span>
      </div>
      <div className="no-scrollbar flex gap-3 overflow-x-auto">
        {images.map((src, idx) => (
          <button key={src} data-testid={`product-thumb-${idx}`} onClick={() => setI(idx)} className={`render h-20 w-20 shrink-0 rounded-xl border-2 transition-[border-color,opacity] ${idx === i ? "border-walnut" : "border-transparent opacity-70 hover:opacity-100"}`}>
            <img src={src} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

const VariantPicker = ({ variants, value, onChange }) => (
  <div data-testid="variant-picker">
    <p className="text-sm font-semibold">Finish: <span data-testid="variant-selected-name" className="font-normal text-muted-foreground">{variants.find((v) => v.key === value)?.name}</span></p>
    <div className="mt-3 flex flex-wrap gap-3">
      {variants.map((v) => (
        <button
          key={v.key} data-testid={`variant-option-${v.key}`} onClick={() => onChange(v.key)} aria-pressed={v.key === value}
          className={`flex items-center gap-2.5 rounded-full border py-1.5 pl-1.5 pr-4 text-sm font-semibold transition-[border-color,background-color,transform] active:scale-[0.98] ${v.key === value ? "border-walnut bg-card shadow-card" : "border-jute/70 bg-card/50 hover:border-walnut/40"}`}
        >
          <span style={{ backgroundColor: v.swatch }} className={`h-7 w-7 rounded-full ${v.key === value ? "ring-2 ring-walnut ring-offset-2 ring-offset-card" : ""}`} />
          {v.name}
        </button>
      ))}
    </div>
  </div>
);

const Addons = ({ addons, selected, toggle }) => (
  <div data-testid="addons-section" className="card p-5 sm:p-6">
    <p className="t-h4">Build it your way</p>
    <p className="t-small mt-1">Modules slide into any shelf bay and match the finish you picked. Add them now or any time later.</p>
    <ul className="mt-4 space-y-2">
      {addons.map((a) => {
        const on = selected.includes(a.slug);
        return (
          <li key={a.slug} className={`flex items-center gap-3 rounded-xl border p-2.5 transition-colors ${on ? "border-sheesham/50 bg-sheesham/5" : "border-jute/50"}`}>
            <Checkbox id={`addon-${a.slug}`} data-testid={`addon-checkbox-${a.slug}`} checked={on} onCheckedChange={() => toggle(a.slug)} className="ml-1" />
            <div className="render h-14 w-14 shrink-0 rounded-lg"><img src={a.render || a.images[0]} alt="" className="h-full w-full object-cover" /></div>
            <label htmlFor={`addon-${a.slug}`} className="flex-1 cursor-pointer text-sm">
              <span className="font-semibold">{a.name}</span>
              <span className="t-small block">{a.features[0]} · {buildLabel(a.build_minutes)}</span>
              <Link to={`/product/${a.slug}`} data-testid={`addon-link-${a.slug}`} className="text-xs font-semibold text-sheesham underline-offset-2 hover:underline">Details</Link>
            </label>
            <span className="price text-sm">+{inr(a.price)}</span>
          </li>
        );
      })}
    </ul>
  </div>
);

const MODULE_KEY = { "pustak-drawer": "drawer", "pustak-lift-top": "lift", "pustak-door": "door" };
const isModule = (p) => p.category === "modules";

const FitsRack = () => (
  <Link to="/product/pustak-rack" data-testid="fits-rack-link" className="card card-hover flex items-center gap-4 p-4">
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-sand text-sheesham"><Puzzle className="h-5 w-5" /></span>
    <span className="text-sm"><span className="font-semibold">Fits the Pustak Rack.</span> Slides into any of the six 96.4 x 23.5 x 22.2 cm shelf bays. Pick the same Oak or Walnut finish as your rack.</span>
  </Link>
);

const Facts = ({ p }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
    {[[Timer, buildLabel(p.build_minutes)], [Users, p.people === 1 ? "1 person" : `${p.people} people`], [ShieldCheck, `${p.warranty_years}-year warranty`], p.weight_kg ? [Weight, `${p.weight_kg} kg box`] : [Ruler, isModule(p) ? "Fits any shelf bay" : "212.4 cm tall"]].map(([I, t]) => (
      <div key={t} className="flex flex-col gap-2 rounded-2xl bg-card p-3.5 text-sm font-medium shadow-card"><I className="h-4 w-4 text-sheesham" />{t}</div>
    ))}
  </div>
);

const Details = ({ p }) => (
  <Accordion type="multiple" defaultValue={["box", "spec"]} className="card divide-y divide-jute/50 px-5 sm:px-6">
    <AccordionItem value="box" className="border-0">
      <AccordionTrigger data-testid="accordion-in-box" className="py-4 text-base font-semibold hover:no-underline">What's in the box</AccordionTrigger>
      <AccordionContent>
        <ul data-testid="in-box-list" className="space-y-2">
          {p.in_box.map((x) => <li key={x} className="flex gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-neem" />{x}</li>)}
        </ul>
      </AccordionContent>
    </AccordionItem>
    <AccordionItem value="spec" className="border-0">
      <AccordionTrigger data-testid="accordion-specs" className="py-4 text-base font-semibold hover:no-underline">Size and material</AccordionTrigger>
      <AccordionContent className="space-y-3 text-sm">
        <SizeDrawing modules={isModule(p) ? [MODULE_KEY[p.slug]] : undefined} />
        <table data-testid="spec-table" className="w-full text-left">
          <tbody>
            {[...p.specs, ["Material", p.material]].map(([k, v]) => (
              <tr key={k} className="border-b border-jute/40 last:border-0"><th className="py-2 pr-4 font-normal text-muted-foreground">{k}</th><td className="py-2 font-medium">{v}</td></tr>
            ))}
          </tbody>
        </table>
      </AccordionContent>
    </AccordionItem>
    <AccordionItem value="delivery" className="border-0">
      <AccordionTrigger data-testid="accordion-delivery" className="py-4 text-base font-semibold hover:no-underline">Delivery, payment and returns</AccordionTrigger>
      <AccordionContent className="space-y-2 text-sm text-muted-foreground">
        <p>24 Hrs free delivery, anywhere in Hyderabad and Secunderabad. We deliver only within Hyderabad for now.</p>
        <p>{isModule(p) ? "Ships flat in a slim box. Build it in the bay with the same hex key as your rack." : "Ships flat, with the longest part 212.4 cm, so it fits most lifts and staircases."}</p>
        <p>Pay by cash or UPI when the box arrives.</p>
        <p>Returns within 10 days if unbuilt or faulty. Missing parts sent free, within 2 working days in Hyderabad.</p>
        <p className="flex flex-wrap gap-x-3 font-semibold text-foreground"><Link to="/policies/shipping" className="underline underline-offset-2">Shipping</Link><Link to="/policies/returns" className="underline underline-offset-2">Returns</Link><Link to="/policies/payments" className="underline underline-offset-2">Payments</Link><Link to="/policies/warranty" className="underline underline-offset-2">Warranty</Link></p>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

export default function ProductPage() {
  const { slug } = useParams();
  const { data, isLoading, isError } = useProduct(slug);
  const { addToCart, toggleWishlist, inWishlist } = useStore();
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState([]);
  const [sp, setSp] = useSearchParams();
  useEffect(() => { setQty(1); setSelected([]); }, [slug]);
  useEffect(() => { document.body.classList.add("has-sticky-bar"); return () => document.body.classList.remove("has-sticky-bar"); }, []);

  if (isLoading) return <div className="container-hw grid gap-10 py-12 lg:grid-cols-2"><Seo title="Pustak Rack – DIY Modular Bookshelf | H&W Furniture" description="DIY modular furniture from H&W Furniture, Hyderabad." path={`/product/${slug}`} /><Skeleton className="aspect-square rounded-[2rem]" /><Skeleton className="h-96 rounded-2xl" /></div>;
  if (isError || !data) return <div className="container-hw py-24 text-center"><Seo title="Piece not found | H&W Furniture" description="This piece is not available." path="/shop" noindex /><h1 className="t-h1">We couldn't find that piece.</h1><Link to="/shop" className="btn-sheesham mt-8">Back to shop</Link></div>;

  const { product: p, addons, related } = data;
  const variant = p.variants.find((v) => v.key === sp.get("finish")) || p.variants[0];
  const render = variant?.render || p.render;
  const images = [render, ...(variant?.images || p.images)].filter(Boolean);
  const chosen = addons.filter((a) => selected.includes(a.slug)).map((a) => ({ ...a, variant: variant?.key }));
  const total = p.price * qty + chosen.reduce((s, a) => s + a.price, 0);
  const toggle = (s) => setSelected((x) => (x.includes(s) ? x.filter((y) => y !== s) : [...x, s]));
  const add = () => addToCart([{ ...p, qty, variant: variant?.key }, ...chosen]);
  const saved = inWishlist(p.slug);
  const pickVariant = (k) => setSp((prev) => { const n = new URLSearchParams(prev); n.set("finish", k); return n; }, { replace: true });
  const kind = isModule(p) ? "Pustak Rack add-on module" : "7-shelf DIY modular bookshelf";

  return (
    <div className="pb-24 lg:pb-0">
      <Seo
        title={`${p.name}${variant ? ` in ${variant.name}` : ""} – ${isModule(p) ? "Pustak Rack Add-on Module" : "7-Shelf DIY Modular Bookshelf"} | H&W Furniture`}
        description={`${p.name}: ${p.tagline} ${isModule(p) ? "Flat-pack module for the Pustak Rack" : "Flat-pack modular bookshelf"}${p.variants.length ? ` in ${p.variants.map((v) => v.name).join(" or ")}` : ""}, build it yourself, no carpenter needed. ${inr(p.price)}${p.gst_exclusive ? " + GST" : ""}. 24 Hrs free delivery in Hyderabad.`}
        path={`/product/${p.slug}`}
        type="product"
        jsonLd={[productLd(p), breadcrumbLd([["Home", "/"], ["Shop", "/shop"], [CATEGORY_NAMES[p.category], `/shop/${p.category}`], [p.name, `/product/${p.slug}`]])]}
      />
      <div className="container-hw grid gap-8 py-6 lg:grid-cols-12 lg:gap-14 lg:py-12">
        <div className="min-w-0 lg:col-span-7"><Gallery images={images} name={variant ? `${p.name} in ${variant.name}` : p.name} /></div>
        <div className="min-w-0 space-y-6 lg:col-span-5">
          <nav className="t-small flex flex-wrap items-center gap-1.5"><Link to="/shop" className="hover:text-foreground">Shop</Link><span>/</span><Link to={`/shop/${p.category}`} className="hover:text-foreground">{CATEGORY_NAMES[p.category]}</Link></nav>
          <div>
            <p className="eyebrow mb-3">{p.badge === "New" && "New · "}{kind}</p>
            <h1 data-testid="product-title" className="t-h1">{p.name}</h1>
            <p className="t-lead mt-3">{p.tagline}</p>
            <p data-testid="product-spec-line" className="spec-line mt-3">{[p.dimensions, p.weight_kg ? `${p.weight_kg} kg` : null, p.build_minutes ? `${p.build_minutes} min build` : null].filter(Boolean).join(" · ")}</p>
            <a href="#reviews" className="mt-3 inline-flex items-center gap-2 text-sm"><Stars value={p.rating} /> {p.review_count ? `${p.rating} (${p.review_count} reviews)` : "No reviews yet"}</a>
          </div>
          <div className="card p-5">
            <div className="flex flex-wrap items-baseline gap-3">
              <span data-testid="product-detail-price" className="price text-4xl">{inr(p.price)}</span>
              {pctOff(p) > 0 && <span className="mrp text-lg">{inr(p.mrp)}</span>}
              {pctOff(p) > 0 && <span className="rounded-full bg-sheesham px-2.5 py-1 text-xs font-semibold text-linen">Save {inr(p.mrp - p.price)} ({pctOff(p)}%)</span>}
            </div>
            <p data-testid="product-tax-note" className="small-print mt-2">
              {p.gst_exclusive ? `Excluding tax (${p.gst_rate}% GST, ${inr(Math.round((p.price * p.gst_rate) / 100))}, added at checkout).` : "Inclusive of all taxes."} 24 Hrs free delivery across Hyderabad.
            </p>
          </div>
          {variant && <VariantPicker variants={p.variants} value={variant.key} onChange={pickVariant} />}
          {p.features.length > 0 && (
            <ul data-testid="product-features" className="grid gap-3 border-y border-jute/50 py-5 text-sm sm:grid-cols-2">
              {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-neem" />{f}</li>)}
            </ul>
          )}
          <Facts p={p} />
          {isModule(p) && <FitsRack />}
          {addons.length > 0 && <Addons addons={addons} selected={selected} toggle={toggle} />}
          <div className="flex items-center gap-3">
            <QtyStepper qty={qty} onChange={(q) => setQty(Math.max(1, Math.min(10, q)))} testid="product-qty" size="lg" />
            <button data-testid="add-to-cart-button" onClick={add} className="btn-sheesham min-h-12 min-w-0 flex-1 whitespace-normal px-3 py-2 text-center text-sm leading-snug sm:text-base">
              Add to cart{chosen.length > 0 && ` with ${chosen.length} add-on${chosen.length > 1 ? "s" : ""}`} · {inr(total)}
            </button>
            <button data-testid="product-wishlist-button" onClick={() => toggleWishlist(p)} aria-label="Save to wishlist" className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-jute/80 bg-card transition-[border-color,transform] hover:border-walnut active:scale-95">
              <Heart className={`h-5 w-5 ${saved ? "fill-sheesham text-sheesham" : ""}`} />
            </button>
          </div>
          <PincodeChecker />
          <ShareBar
            path={`/product/${p.slug}${variant ? `?finish=${variant.key}` : ""}`}
            title={`${p.name} by H&W Furniture`}
            text={`Look at the ${p.name}${variant ? ` in ${variant.name}` : ""}: ${isModule(p) ? "an add-on module for the Pustak Rack" : "a DIY modular bookshelf"} from H&W Furniture, Hyderabad.`}
            image={images[0].startsWith("http") ? images[0] : `${SITE_URL}${images[0]}`}
            testid="product-share"
          />
          <p className="t-body text-muted-foreground">{p.description}</p>
          <Details p={p} />
        </div>
      </div>
      <div className="container-hw border-t border-jute/50 py-16"><Reviews product={p} /></div>
      {related.length > 0 && (
        <section className="container-hw py-16">
          <Reveal><p className="eyebrow">Keep building</p><h2 className="t-h2 mt-3">{isModule(p) ? "More modules for the same bays" : `More for the ${CATEGORY_NAMES[p.category].toLowerCase()}`}</h2></Reveal>
          <div className="mt-8 grid grid-cols-2 gap-x-5 gap-y-12 lg:grid-cols-4">{related.map((r) => <ProductCard key={r.slug} product={r} />)}</div>
        </section>
      )}
      <div className="glass fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-4 border-t border-jute/60 px-4 py-3 lg:hidden" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <div><p className="t-small">{p.name}</p><p className="price text-lg">{inr(total)}</p></div>
        <button data-testid="mobile-add-to-cart-button" onClick={add} className="btn-sheesham"><Truck className="h-4 w-4" />Add to cart</button>
      </div>
    </div>
  );
}
